import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { colors } from "theme/palette";

import Alert from "components/Alert";
import Button from "components/Button";
import ButtonBase from "components/ButtonBase";
import { LinearProgress } from "components/Loading";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import {
  IValueCreator,
  useSuperuserValueCreators,
} from "entities/Superuser/sdk";

export interface IUserSelect {
  onStepAdvance: ({ user }: { user: IValueCreator }) => void;
  onStepBack: () => void;
  selectedDealId: number;
}

const UserSelect = ({
  onStepAdvance,
  onStepBack,
  selectedDealId,
}: IUserSelect) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<IValueCreator | null>(null);

  const handleTextInputChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const { data: users, isValidating: isLoadingUsers } =
    useSuperuserValueCreators({
      dealId: selectedDealId,
    });

  const filteredUsers = useMemo(() => {
    if (_.isNil(searchTerm)) return users;

    const filteredSearchTerm = searchTerm.toLowerCase();

    return users?.filter((user) => {
      return (
        user.first_name.toLowerCase().includes(filteredSearchTerm) ||
        user.last_name.toLowerCase().includes(filteredSearchTerm) ||
        user.email.toLowerCase().includes(filteredSearchTerm)
      );
    });
  }, [searchTerm, users]);

  return (
    <Stack
      flexGrow={1}
      spacing={1}
      marginTop={1}
      sx={{
        height: "calc(100vh - 245px)",
      }}
    >
      <Alert severity="info">
        Select a user you'd like to be the value creator of the checklist file
        import.
      </Alert>

      <TextField
        autoFocus
        size="small"
        defaultValue={searchTerm}
        onChange={handleTextInputChange}
        data-testid="edit-data-cell-value-field"
        placeholder="Please select a user"
      />

      <Stack
        sx={{
          borderRadius: "4px",
          overflowY: "auto",
          flexGrow: 1,
          border: `1px solid ${colors.gray40}`,
        }}
      >
        {isLoadingUsers && (
          <LinearProgress sx={{ position: "sticky", left: 0 }} />
        )}

        {filteredUsers?.map((user) => (
          <ButtonBase
            key={user.id}
            onClick={() => setSelectedUser(user)}
            sx={{
              padding: 1,
              transition: "backgroundColor 0.2s ease",
              backgroundColor:
                selectedUser?.id === user.id ? colors.blue20 : "transparent",
              alignItems: "flex-start",
              flexDirection: "column",
              "&:hover": {
                backgroundColor: colors.blue20,
              },
            }}
          >
            <Text fontWeight={600}>
              {user.first_name} {user.last_name}
            </Text>
            <Text fontWeight={400}>{user.email}</Text>
          </ButtonBase>
        ))}
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Button onClick={onStepBack}>Back</Button>

        <Button
          disabled={_.isNil(selectedUser)}
          onClick={
            _.isNil(selectedUser)
              ? undefined
              : () => onStepAdvance({ user: selectedUser })
          }
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
};

export default UserSelect;
